import {
  componentNames,
  HYBRID_LAYOUT_CONSTANTS,
} from '../../../utils/constants'
import { HybridLayoutLookupField } from './HybridLookupFields'
import { isEmpty } from 'lodash'
import {
  HybridLayoutCheckboxField,
  HybridLayoutDateTimeField,
  HybridLayoutDefaultField,
  HybridLayoutDropdownField,
  HybridLayoutEmailField,
  HybridLayoutMultiLineField,
  HybridLayoutPhoneNumberField,
  HybridLayoutRadioField,
} from './HybirdLayoutNormalFields'
import { HybridLayoutFieldCategoryField } from './HybridAdvancedFields'
import React from 'react'
import { HybridLayoutFieldMapOnTypePropsType } from '../hybridLayout.types'
import { ElementType } from '../../../types/layout.types'
import { selectMergedBehaviorObj } from '@app-store'
import { useAppSelector } from '../../../utils/agent.store'

export const HybridLayoutFieldMapOnType: React.FC<HybridLayoutFieldMapOnTypePropsType> =
  React.memo(
    ({
      element,
      fullLayout,
      isAssociated,
      associatedObject,
      formUniqueId,
      mode,
    }) => {
      const behaviorObj = useAppSelector(selectMergedBehaviorObj)

      const elementKeyName = element?.parent
        ? `${HYBRID_LAYOUT_CONSTANTS.NESTING_DATA_KEY}.${element?.parent?.lookupKeyName}.${element?.keyName}`
        : element?.keyName

      const originalKeyName = element?.parent
        ? `${element?.parent?.lookupKeyName}.${element?.keyName}`
        : element?.keyName

      const finalElement = {
        ...element,
        keyName: isAssociated
          ? `${associatedObject?.associatedPath}.${elementKeyName}`
          : elementKeyName,
      }

      const isBehaviorDisabled =
        behaviorObj.disabled?.some(
          (key) =>
            finalElement.keyName === key ||
            finalElement.keyName?.endsWith(`.${key}`)
        ) ?? false

      const fieldObject = element?.parent
        ? fullLayout?.layout_data?.added_fields?.[
            element?.parent?.lookupKeyName || ''
          ]?.added_fields?.[element?.keyName || '']
        : fullLayout?.layout_data?.added_fields?.[element?.keyName || '']

      const elementFinalObject = {
        ...finalElement,
        meta: {
          ...finalElement?.meta,
          ...fieldObject,
          originalKeyName:
            isAssociated && associatedObject?.lookupKeyName
              ? `${associatedObject?.lookupKeyName}.${originalKeyName}`
              : originalKeyName,
        },
      } as ElementType

      if (!element) return null
      switch (element?.elementName) {
        case componentNames?.SINGLE_LOOKUP:
          return (
            <HybridLayoutLookupField
              element={elementFinalObject}
              fullLayout={fullLayout}
              isAssociated={isAssociated}
              associatedObject={associatedObject}
              formUniqueId={formUniqueId}
              mode={mode}
            />
          )
        case componentNames?.HEADER:
        case componentNames?.PARAGRAPH:
          return <div>{element?.meta?.label}</div>
        case componentNames?.UNIQUE_ID:
          return isEmpty(element) ? null : (
            <HybridLayoutDefaultField
              element={elementFinalObject}
              isDisabled={true}
              fullLayout={fullLayout}
              mode={mode}
            />
          )
        case componentNames.CATEGORY:
          return (
            <HybridLayoutFieldCategoryField
              element={elementFinalObject}
              entityName={fullLayout?.entity_name}
              fullLayout={fullLayout}
              mode={mode}
            />
          )
        case componentNames.DROPDOWN:
          return isEmpty(element) ? null : (
            <HybridLayoutDropdownField
              fullLayout={fullLayout}
              element={elementFinalObject}
              mode={mode}
              isDisabled={isBehaviorDisabled || undefined}
            />
          )
        case componentNames.DATE_TIME:
          return (
            <HybridLayoutDateTimeField
              element={elementFinalObject}
              mode={mode}
              fullLayout={fullLayout}
              isDisabled={isBehaviorDisabled || undefined}
            />
          )
        case componentNames.CHECKBOX:
          return (
            <HybridLayoutCheckboxField
              element={finalElement}
              isDisabled={isBehaviorDisabled || undefined}
            />
          )
        case componentNames.RADIO_GROUP:
          return (
            <HybridLayoutRadioField
              element={elementFinalObject}
              fullLayout={fullLayout}
              mode={mode}
              isDisabled={isBehaviorDisabled || undefined}
            />
          )
        case componentNames.SINGLE_LINE:
        case componentNames.NUMBER:
          return isEmpty(element) ? null : (
            <HybridLayoutDefaultField
              fullLayout={fullLayout}
              element={elementFinalObject}
              mode={mode}
              isDisabled={isBehaviorDisabled || undefined}
            />
          )

        case componentNames.MULTI_LINE:
          return isEmpty(element) ? null : (
            <HybridLayoutMultiLineField
              fullLayout={fullLayout}
              element={elementFinalObject}
              mode={mode}
              isDisabled={isBehaviorDisabled || undefined}
            />
          )
        case componentNames.EMAIL:
          return isEmpty(element) ? null : (
            <HybridLayoutEmailField
              fullLayout={fullLayout}
              element={elementFinalObject}
              mode={mode}
              isDisabled={isBehaviorDisabled || undefined}
            />
          )
        case componentNames.PHONE_NUMBER:
        case componentNames.FAX_NUMBER:
          return isEmpty(element) ? null : (
            <HybridLayoutPhoneNumberField
              fullLayout={fullLayout}
              element={elementFinalObject}
              mode={mode}
              isDisabled={isBehaviorDisabled || undefined}
            />
          )
        case componentNames.NPI:
        case componentNames.DEA:
        case componentNames.DATE:
        case componentNames.SSN:
          return isEmpty(element) ? null : (
            <HybridLayoutDefaultField
              fullLayout={fullLayout}
              element={elementFinalObject}
              mode={mode}
              isDisabled={true}
            />
          )
        default:
          return null
      }
    }
  )
HybridLayoutFieldMapOnType.displayName = 'HybridLayoutFieldMapOnType'
